<template>
  <v-form ref="form" v-model="valid">
    <v-row dense>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.borrowerName"
          placeholder="VD: Nguyễn Văn A..."
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng nhập tên người mượn']"
        >
          <template v-slot:label>
            Họ tên người mượn <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.borrowerPhone"
          label="Số điện thoại liên hệ"
          outlined
          dense
        />
      </v-col>

      <v-col cols="12" md="3">
        <v-text-field
          v-model="formData.borrowDate"
          type="date"
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng chọn ngày']"
        >
          <template v-slot:label>
            Ngày mượn <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="formData.borrowTime"
          type="time"
          label="Giờ mượn"
          outlined
          dense
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="formData.expectedReturnDate"
          type="date"
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng chọn ngày trả']"
        >
          <template v-slot:label>
            Ngày trả dự kiến <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="formData.expectedReturnTime"
          type="time"
          label="Giờ trả dự kiến"
          outlined
          dense
        />
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="formData.purpose"
          placeholder="VD: Dạy học tiết Tin học lớp 10A1, Tổ chức hội thảo..."
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng nhập mục đích']"
        >
          <template v-slot:label>
            Mục đích sử dụng <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>

      <!-- Danh sách thiết bị mượn -->
      <v-col cols="12">
        <v-card outlined class="pa-3 grey lighten-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="text-subtitle-2 font-weight-bold primary--text text-uppercase">
              Thiết bị đăng ký mượn ({{ formData.items.length }})
            </span>
            <v-btn small color="primary" depressed @click="addItem">
              <v-icon small left>mdi-plus</v-icon> Thêm thiết bị
            </v-btn>
          </div>

          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th style="width: 30%">Danh mục thiết bị</th>
                  <th style="width: 35%">Thiết bị (Khả dụng) <span class="red--text font-weight-bold">*</span></th>
                  <th style="width: 15%">Số lượng mượn <span class="red--text font-weight-bold">*</span></th>
                  <th style="width: 15%">Đơn vị</th>
                  <th style="width: 45px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="formData.items.length === 0">
                  <td colspan="5" class="text-center grey--text py-3">
                    Chưa có thiết bị nào. Nhấn "Thêm thiết bị" để chọn.
                  </td>
                </tr>
                <tr v-for="(item, index) in formData.items" :key="index">
                  <td class="py-2">
                    <v-autocomplete
                      v-model="item.category"
                      :items="categories"
                      item-text="name"
                      item-value="id"
                      placeholder="Chọn danh mục"
                      outlined
                      dense
                      clearable
                      hide-details
                      @change="onCategoryChanged(item)"
                    >
                      <template v-slot:item="{ item: cat }">
                        <div :style="{ paddingLeft: (cat.depth || 0) * 12 + 'px' }" class="d-flex align-center">
                          <v-icon small color="primary" class="mr-1">mdi-folder-outline</v-icon>
                          <span>{{ cat.name }}</span>
                        </div>
                      </template>
                    </v-autocomplete>
                  </td>
                  <td class="py-2">
                    <v-select
                      v-model="item.asset"
                      :items="getFilteredAssets(item)"
                      :item-text="formatAssetLabel"
                      item-value="id"
                      placeholder="Chọn thiết bị"
                      outlined
                      dense
                      clearable
                      hide-details
                      @change="onAssetSelected(item)"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="item.quantity"
                      type="number"
                      min="1"
                      :max="item.maxAvailable || 999"
                      outlined
                      dense
                      hide-details
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model="item.unit"
                      readonly
                      outlined
                      dense
                      hide-details
                    />
                  </td>
                  <td class="text-center">
                    <v-btn icon small color="error" @click="removeItem(index)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-textarea
          v-model="formData.note"
          label="Ghi chú thêm"
          outlined
          dense
          rows="2"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { mapGetters } from 'vuex'
import { Asset, AssetCategory } from '@/plugins/api'

export default {
  name: 'BorrowForm',
  data() {
    const today = new Date().toISOString().substring(0, 10)
    return {
      valid: true,
      categories: [],
      assetList: [],
      formData: {
        borrowerName: '',
        borrowerPhone: '',
        borrowDate: today,
        borrowTime: '08:00',
        expectedReturnDate: today,
        expectedReturnTime: '17:00',
        purpose: '',
        note: '',
        borrower: null,
        items: []
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['role', 'profile', 'user']),
    isTeacher() {
      return this.role && (this.role.name === 'Teacher' || this.role.type === 'teacher')
    }
  },
  async created() {
    if (this.user) {
      this.formData.borrower = this.user.id || this.user._id
    }
    if (this.profile) {
      this.formData.borrowerName = this.profile.name || ''
      this.formData.borrowerPhone = this.profile.phone || this.profile.phoneNumber || ''
    }
    if (this.formData.items.length === 0) {
      this.addItem()
    }
    try {
      const [categories, assets] = await Promise.all([
        AssetCategory.search({ _limit: -1, isDelete: false }),
        Asset.search({ _limit: -1, isDelete: false })
      ])
      this.categories = categories || []
      this.assetList = assets || []
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    formatAssetLabel(a) {
      return `${a.name} (Tồn khả dụng: ${a.availableQuantity || 0} ${a.unit || ''})`
    },
    getFilteredAssets(item) {
      if (!item.category) {
        return this.assetList
      }
      return this.assetList.filter(a => {
        if (!a.category) return false
        const catId = typeof a.category === 'object' ? a.category.id : a.category
        return catId === item.category
      })
    },
    addItem() {
      this.formData.items.push({
        category: null,
        asset: null,
        assetName: '',
        quantity: 1,
        maxAvailable: 1,
        unit: 'Chiếc'
      })
    },
    removeItem(index) {
      this.formData.items.splice(index, 1)
    },
    onCategoryChanged(item) {
      if (item.asset) {
        const found = this.assetList.find(a => a.id === item.asset)
        const catId = found && found.category ? (typeof found.category === 'object' ? found.category.id : found.category) : null
        if (item.category && catId !== item.category) {
          item.asset = null
          item.assetName = ''
          item.unit = 'Chiếc'
          item.maxAvailable = 1
        }
      }
    },
    onAssetSelected(item) {
      const found = this.assetList.find(a => a.id === item.asset)
      if (found) {
        item.assetName = found.name
        item.unit = found.unit || 'Chiếc'
        item.maxAvailable = found.availableQuantity || 1
        if (!item.category && found.category) {
          item.category = typeof found.category === 'object' ? found.category.id : found.category
        }
      }
    },
    validate() {
      if (this.formData.items.length === 0) {
        this.$alert.error('Vui lòng chọn ít nhất 1 thiết bị cần mượn')
        return false
      }
      for (const it of this.formData.items) {
        if (!it.asset) {
          this.$alert.error('Vui lòng chọn thiết bị cho từng dòng')
          return false
        }
        if (!it.quantity || it.quantity <= 0) {
          this.$alert.error('Số lượng mượn phải lớn hơn 0')
          return false
        }
      }
      return this.$refs.form.validate()
    },
    getData() {
      return {
        ...this.formData,
        items: this.formData.items.map(it => ({
          category: it.category,
          asset: it.asset,
          assetName: it.assetName,
          quantity: it.quantity,
          unit: it.unit
        }))
      }
    }
  }
}
</script>
